import React, { useState, useEffect } from 'react';
import { Users, BookOpen, Clock } from 'lucide-react';

interface StatsData {
  active_users: number;
  quizzes_created: number;
  minutes_played: number;
  change_users: string;
  change_quizzes: string;
  change_minutes: string;
  cached_at: string;
}

const UsageStats = () => {
  const [statsData, setStatsData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get stats URL from environment variable
        const statsUrl = process.env.REACT_APP_STATS_API_URL;
        if (!statsUrl) {
          console.warn('REACT_APP_STATS_API_URL not configured, using mock data');
          setLoading(false);
          return;
        }

        const response = await fetch(statsUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch stats');
        }

        const data: StatsData = await response.json();
        setStatsData(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const stats = statsData
    ? [
        {
          label: 'Active Users',
          value: formatNumber(statsData.active_users),
          change: statsData.change_users,
          icon: Users,
          color: 'text-slate-700',
          bgColor: 'bg-slate-100',
          borderColor: 'border-slate-200',
        },
        {
          label: 'Quizzes Created',
          value: formatNumber(statsData.quizzes_created),
          change: statsData.change_quizzes,
          icon: BookOpen,
          color: 'text-blue-700',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
        },
        {
          label: 'Minutes Played',
          value: formatNumber(statsData.minutes_played),
          change: statsData.change_minutes,
          icon: Clock,
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
        },
      ]
    : [
        {
          label: 'Active Users',
          value: '12,345',
          change: '+12%',
          icon: Users,
          color: 'text-slate-700',
          bgColor: 'bg-slate-100',
          borderColor: 'border-slate-200',
        },
        {
          label: 'Quizzes Created',
          value: '3,821',
          change: '+18%',
          icon: BookOpen,
          color: 'text-blue-700',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
        },
        {
          label: 'Minutes Played',
          value: '45,678',
          change: '+24%',
          icon: Clock,
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
        },
      ];

  return (
    <div className="py-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Up and coming!
        </h2>
        <p className="text-gray-600 text-lg">
          Real-time statistics from the last 24 hours
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`relative overflow-hidden rounded-lg border ${stat.borderColor} bg-white p-6 shadow hover:shadow-md transition-all duration-200`}
            >
              <div className="absolute top-0 right-0 -mr-4 -mt-4 h-24 w-24 rounded-full bg-gradient-to-br from-white/40 to-transparent" />

              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded ${stat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-1 rounded">
                  {stat.change}
                </span>
              </div>

              <div className="space-y-1">
                <p className="text-3xl font-bold text-gray-900">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600 font-medium">
                  {stat.label}
                </p>
              </div>

              <div className="mt-4 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${stat.bgColor} rounded-full`}
                  style={{ width: '75%' }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          * Statistics update every 5 minutes
        </p>
      </div>
    </div>
  );
};

export default UsageStats;